import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/api";
import { Seo } from "@/components/Seo";
import { MaskedLine } from "@/components/Motion";
import { WatermarkOverlay } from "@/components/WatermarkOverlay";
import { ShareButton } from "@/components/ShareButton";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/utils";

export default function RiderProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/riders/${slug}`).then((response) => setProfile(response.data)).catch(() => setProfile(false));
  }, [slug]);

  if (profile === false) {
    return <div data-testid="rider-not-found" className="flex min-h-screen items-center justify-center bg-onyx px-4 text-center text-zinc-400">Rider profile not found.</div>;
  }
  if (!profile) return <div data-testid="rider-loading-state" className="min-h-screen bg-onyx pt-40 text-center font-mono text-xs uppercase tracking-[0.3em] text-gold">Loading profile</div>;

  const events = [...new Set(profile.photos.map((photo) => photo.event_title))];
  const profileUrl = window.location.href;
  const shareText = `${profile.name} — ${profile.photo_count} frames from ${events.join(" & ")} on mellowjmedia`;
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(profileUrl)}`;
  const coverImage = profile.photos[0] ? `${window.location.origin}${profile.photos[0].preview_url}` : undefined;

  return (
    <div data-testid="rider-profile-page" className="min-h-screen bg-onyx pt-32">
      <Seo title={profile.name} description={`Every ${profile.name} frame in the mellowjmedia archive — ${profile.photo_count} photos across ${events.length} events.`} image={coverImage} />
      <section className="mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12">
        <Link data-testid="rider-back-link" to="/riders" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.28em] text-titanium hover:text-gold"><ArrowLeft className="h-4 w-4" /> All riders & drivers</Link>
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.42em] text-gold">Rider profile</p>
        <h1 className="mt-5 font-display text-5xl font-light leading-none text-white sm:text-7xl">
          <MaskedLine>{profile.name}</MaskedLine>
        </h1>
        <div className="mt-6 flex flex-wrap gap-5 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          <span data-testid="rider-frame-count" className="text-gold">{profile.photo_count} frame{profile.photo_count === 1 ? "" : "s"}</span>
          <span>{events.length} event{events.length === 1 ? "" : "s"}</span>
          <span>{events.join(" · ")}</span>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <ShareButton testId="rider-share-button" title={`${profile.name} on mellowjmedia`} text={shareText} url={profileUrl} />
          <a data-testid="rider-share-x" href={tweetUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">
            Post on X
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-4 py-16 sm:px-8 lg:px-12">
        <div className="columns-1 gap-5 space-y-5 sm:columns-2 lg:columns-3">
          {profile.photos.map((photo, index) => (
            <motion.article key={photo.id} data-testid={`rider-photo-${photo.id}`} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.04, duration: 0.6 }} className="group relative break-inside-avoid overflow-hidden border border-white/10 bg-carbon">
              <Link data-testid={`rider-photo-event-link-${photo.id}`} to={`/events/${photo.event_slug}`} className="relative block">
                <img src={photo.preview_url} alt={`${photo.title} featuring ${profile.name}`} loading="lazy" className="w-full object-cover transition duration-700 group-hover:scale-[1.025]" />
                <WatermarkOverlay />
              </Link>
              <div className="p-5">
                <h3 className="font-display text-2xl text-white">{photo.title}</h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">{photo.vehicle || photo.event_category}</p>
                <Link data-testid={`rider-photo-event-${photo.id}`} to={`/events/${photo.event_slug}`} className="mt-4 block font-lux text-sm uppercase tracking-[0.16em] text-gold hover:text-titanium">{photo.event_title}</Link>
                <div className="mt-3 flex flex-wrap gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{photo.event_location}</span>
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{photo.event_date}</span>
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="font-display text-2xl text-white">${formatPrice(photo.price)}</span>
                  <button data-testid={`rider-buy-photo-${photo.id}`} onClick={() => addItem({ item_type: "photo", item_id: photo.id, title: photo.title, price: photo.price })} className="border border-gold/35 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-onyx">
                    Buy frame
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
